// Re-usable roster components
import React, { useMemo, useState } from 'react';
import { X, Trash2, Activity } from 'lucide-react';
import { IOSButton } from '../ui/custom';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from '../../hooks/use-toast';
import { normalizeUmlauts } from '../../utils/format';

const calculateDuration = (start: any, end: any) => {
    if (!start || !end) return 0;
    try {
        const [h1, m1] = start.split(':').map(Number);
        const [h2, m2] = end.split(':').map(Number);
        return Math.max(0, (h2 + m2/60) - (h1 + m1/60));
    } catch (e) {
        return 0;
    }
};

export const ShiftModal = ({ isOpen, onClose, onSave, onDelete, initialData, selectedDate, selectedUser, users }: any) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    
    if (!isOpen) return null;
    
    const handlePreset = (e: any, type: any) => {
        e.preventDefault();
        e.stopPropagation();
        let start = '', end = '';
        if (type === 'früh') { start = '09:00'; end = '13:00'; }
        else if (type === 'spät') { start = '13:00'; end = '18:00'; }
        else if (type === 'freitag_frueh') { start = '09:00'; end = '12:00'; }
        else if (type === 'freitag_spaet') { start = '12:00'; end = '15:00'; }
        else if (type === 'urlaub') { start = ''; end = ''; }
        
        const form: any = document.getElementById('shift-form');
        const userId = form.userId ? form.userId.value : selectedUser?.id;
        if (!userId) { toast({ title: 'Bitte Mitarbeiter wählen', variant: 'destructive' }); return; }
        onSave({ type, start, end, userId });
    };
    
    const handleCustomSubmit = (e: any) => {
        e.preventDefault();
        const fd = new FormData(e.target);
        if (!fd.get('userId') && !selectedUser) { toast({ title: 'Bitte Mitarbeiter wählen', variant: 'destructive' }); return; }
        onSave({ type: 'custom', start: fd.get('start'), end: fd.get('end'), userId: fd.get('userId') || selectedUser.id });
    };
    
    return (
        <div className={`fixed inset-0 z-[80] flex items-center justify-center p-4 backdrop-blur-xl animate-fade-in ${
            isDark ? 'bg-slate-950/70' : 'bg-slate-900/20'
        }`} onClick={onClose}>
            <div className={`relative w-full max-w-sm overflow-hidden rounded-3xl border px-6 py-6 shadow-[0_20px_80px_rgba(0,0,0,0.45)] ${
                isDark ? 'border-white/10 bg-white/10 text-white' : 'border-slate-200 bg-white text-slate-900'
            }`} onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Schichtplanung</h3>
                        <p className={`text-xs ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{selectedDate}</p>
                    </div>
                    <button onClick={onClose} className={`p-2 rounded-full border transition-colors ${
                        isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-slate-100 border-slate-200 hover:bg-slate-200'
                    }`}><X size={16}/></button>
                </div>
                
                <form id="shift-form" onSubmit={handleCustomSubmit} className="space-y-4">
                    {!selectedUser && (
                        <div className={`p-2 rounded-xl border ${
                            isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'
                        }`}>
                            <label className={`text-xs font-bold uppercase block mb-1 px-1 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>Mitarbeiter</label>
                            <select name="userId" className={`w-full bg-transparent outline-none text-sm font-bold p-1 ${
                                isDark ? 'text-white' : 'text-slate-900'
                            }`}>
                                <option value="" className="text-slate-900">Bitte wählen...</option>
                                {users.map((u: any) => <option key={u.id} value={u.id} className="text-slate-900">{normalizeUmlauts(u.name)}</option>)}
                            </select>
                        </div>
                    )}
                    {selectedUser && <p className={`font-bold text-center p-2 rounded-xl border ${
                        isDark 
                            ? 'bg-blue-500/20 text-blue-100 border-blue-400/30' 
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                    }`}>{normalizeUmlauts(selectedUser.name)}</p>}
                    
                    <div className="grid grid-cols-2 gap-2">
                        <button type="button" onClick={(e) => handlePreset(e, 'früh')} className="bg-gradient-to-br from-blue-500/40 to-cyan-400/20 text-white border border-white/10 hover:from-blue-500/50 hover:to-cyan-400/30 py-3 rounded-xl font-bold text-sm flex flex-col items-center">
                            <span>Früh</span>
                            <span className="text-[9px] text-white/70">09-13</span>
                        </button>
                        <button type="button" onClick={(e) => handlePreset(e, 'spät')} className="bg-gradient-to-br from-indigo-500/40 to-purple-400/20 text-white border border-white/10 hover:from-indigo-500/50 hover:to-purple-400/30 py-3 rounded-xl font-bold text-sm flex flex-col items-center">
                            <span>Spät</span>
                            <span className="text-[9px] text-white/70">13-18</span>
                        </button>
                        <button type="button" onClick={(e) => handlePreset(e, 'freitag_frueh')} className="bg-gradient-to-br from-cyan-500/40 to-emerald-400/20 text-white border border-white/10 hover:from-cyan-500/50 hover:to-emerald-400/30 py-3 rounded-xl font-bold text-sm flex flex-col items-center">
                            <span>FR Früh</span>
                            <span className="text-[9px] text-white/70">09-12</span>
                        </button>
                        <button type="button" onClick={(e) => handlePreset(e, 'freitag_spaet')} className="bg-gradient-to-br from-fuchsia-500/40 to-violet-400/20 text-white border border-white/10 hover:from-fuchsia-500/50 hover:to-violet-400/30 py-3 rounded-xl font-bold text-sm flex flex-col items-center">
                            <span>FR Spät</span>
                            <span className="text-[9px] text-white/70">12-15</span>
                        </button>
                        <button type="button" onClick={(e) => handlePreset(e, 'urlaub')} className="col-span-2 bg-gradient-to-r from-emerald-500/35 via-green-400/25 to-teal-400/25 text-white border border-white/10 hover:from-emerald-500/45 hover:via-green-400/35 hover:to-teal-400/35 py-3 rounded-xl font-bold text-sm">
                            Urlaub
                        </button>
                    </div>
                    
                    <div className="flex gap-2">
                        <div className={`flex-1 p-2 rounded-xl border ${
                            isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'
                        }`}>
                            <label className={`text-[10px] font-bold uppercase ${isDark ? 'text-white/60' : 'text-slate-600'}`}>Start</label>
                            <input name="start" type="time" defaultValue={initialData?.start} className={`w-full bg-transparent font-bold text-sm outline-none ${
                                isDark ? 'text-white' : 'text-slate-900'
                            }`}/>
                        </div>
                        <div className={`flex-1 p-2 rounded-xl border ${
                            isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'
                        }`}>
                            <label className={`text-[10px] font-bold uppercase ${isDark ? 'text-white/60' : 'text-slate-600'}`}>Ende</label>
                            <input name="end" type="time" defaultValue={initialData?.end} className={`w-full bg-transparent font-bold text-sm outline-none ${
                                isDark ? 'text-white' : 'text-slate-900'
                            }`}/>
                        </div>
                    </div>
                    
                    <div className="flex gap-3 pt-2">
                        <IOSButton type="submit" className="flex-1">Speichern</IOSButton>
                        {initialData && <IOSButton onClick={onDelete} variant="danger" className="w-12"><Trash2 size={16}/></IOSButton>}
                    </div>
                </form>
            </div>
        </div>
    );
};

export const RosterStats = ({ users, shifts, currentYear, currentMonth }: any) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [sortBy, setSortBy] = useState<'monat' | 'jahr'>('jahr');

    const stats = useMemo(() => users.map((user: any) => {
        const userShifts = shifts.filter((s: any) => s.userId === user.id);
        const monthHours = userShifts.reduce((sum: any, s: any) => {
            const d = new Date(s.date);
            if (d.getFullYear() === currentYear && d.getMonth() === currentMonth)
                return sum + calculateDuration(s.start, s.end);
            return sum;
        }, 0);
        const yearHours = userShifts.reduce((sum: any, s: any) => {
            const d = new Date(s.date);
            if (d.getFullYear() === currentYear)
                return sum + calculateDuration(s.start, s.end);
            return sum;
        }, 0);
        return { ...user, monthHours, yearHours };
    }).sort((a: any, b: any) =>
        sortBy === 'monat' ? b.monthHours - a.monthHours : b.yearHours - a.yearHours
    ), [users, shifts, currentYear, currentMonth, sortBy]);

    return (
        <div className={`mt-8 rounded-2xl border overflow-hidden p-6 shadow-2xl ${
            isDark 
                ? 'border-white/10 bg-white/10 backdrop-blur-xl text-white' 
                : 'border-slate-200 bg-white text-slate-900'
        }`}>
            <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}><Activity size={20} className={isDark ? 'text-cyan-300' : 'text-cyan-600'}/> Auswertung</h3>
            <table className="w-full text-sm text-left">
                <thead className={`font-bold uppercase text-xs ${
                    isDark ? 'bg-white/5 text-white/70' : 'bg-slate-50 text-slate-600'
                }`}>
                    <tr>
                        <th className="p-3">Mitarbeiter</th>
                        <th className="p-3 text-right">
                            <button type="button" onClick={() => setSortBy('monat')} className={`w-full text-right py-1 rounded hover:underline focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${sortBy === 'monat' ? (isDark ? 'text-cyan-300' : 'text-cyan-600') : ''}`}>
                                Monat {sortBy === 'monat' && '↓'}
                            </button>
                        </th>
                        <th className="p-3 text-right">
                            <button type="button" onClick={() => setSortBy('jahr')} className={`w-full text-right py-1 rounded hover:underline focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${sortBy === 'jahr' ? (isDark ? 'text-cyan-300' : 'text-cyan-600') : ''}`}>
                                Jahr {sortBy === 'jahr' && '↓'}
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody className={isDark ? 'divide-y divide-white/10' : 'divide-y divide-slate-200'}>
                    {stats.map((s: any) => (
                        <tr key={s.id} className={isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}>
                            <td className={`p-3 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{normalizeUmlauts(s.name)}</td>
                            <td className={`p-3 text-right font-bold ${isDark ? 'text-cyan-200' : 'text-cyan-600'}`}>{s.monthHours.toFixed(1)} h</td>
                            <td className={`p-3 text-right ${isDark ? 'text-white/70' : 'text-slate-600'}`}>{s.yearHours.toFixed(1)} h</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
